/**
 * Shared cart store — one code path across every add-to-cart trigger,
 * cart-count badge, and cart drawer.
 *
 * Any element with `data-cart-add` + `data-variant-id` becomes an
 * add-to-cart button when clicked. Multi-variant products skip the
 * AJAX and dispatch `cart:pick-variant` instead so a modal can open.
 * On success a `cart:change` event fires on document with the latest
 * cart snapshot; the header badge, drawer, and per-card counts all
 * listen for it.
 *
 * Called from layout/theme.liquid via <script defer>.
 */
(() => {
  const EVENT_CHANGE = 'cart:change';
  const EVENT_PICK = 'cart:pick-variant';
  const EVENT_OPEN_DRAWER = 'cart:open-drawer';
  const EVENT_ADDED = 'cart:added';

  let cache = null;

  const dispatch = (type, detail) => {
    document.dispatchEvent(new CustomEvent(type, { detail }));
  };

  // Fetch the current cart snapshot. Falls back to a zero-count cart
  // if the request fails (e.g. offline, blocked). Never throws.
  const fetchCart = async () => {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!r.ok) throw new Error('cart fetch failed');
      cache = await r.json();
    } catch (_) {
      cache = { item_count: 0, items: [], total_price: 0 };
    }
    return cache;
  };

  const get = () => cache;

  // Add a variant. Returns the updated cart snapshot on success, or
  // null on failure. Dispatches cart:change either way (safe no-op on
  // failure).
  const add = async (variantId, quantity = 1) => {
    if (!variantId) return null;
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });
      if (!r.ok) throw new Error('cart add failed');
      const line = await r.json();
      await fetchCart();
      dispatch(EVENT_CHANGE, cache);
      dispatch(EVENT_ADDED, { variantId, quantity, line, cart: cache });
      return cache;
    } catch (e) {
      dispatch(EVENT_CHANGE, cache || { item_count: 0, items: [] });
      return null;
    }
  };

  // Change a line's quantity by line index (1-based per Shopify convention).
  // Passing quantity: 0 removes the line.
  const changeLine = async (lineIndex, quantity) => {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity }),
      });
      if (!r.ok) throw new Error('cart change failed');
      cache = await r.json();
      dispatch(EVENT_CHANGE, cache);
      return cache;
    } catch (e) {
      return null;
    }
  };

  // Count of a specific variant currently in cart. Used for per-card badges.
  const quantityForVariant = (variantId) => {
    if (!cache || !Array.isArray(cache.items)) return 0;
    const target = String(variantId);
    return cache.items
      .filter((line) => String(line.variant_id) === target || String(line.id) === target)
      .reduce((sum, line) => sum + (line.quantity || 0), 0);
  };

  // Update every [data-cart-line-count] badge to reflect current cart
  // quantities. Called on cart:change (below) so per-card badges stay
  // live without any per-section wiring.
  const syncCounts = () => {
    document.querySelectorAll('[data-cart-line-count]').forEach((el) => {
      const id = el.dataset.cartLineCount;
      const qty = quantityForVariant(id);
      if (qty > 0) {
        el.textContent = qty;
        el.classList.add('is-visible');
      } else {
        el.textContent = '';
        el.classList.remove('is-visible');
      }
    });
  };
  document.addEventListener(EVENT_CHANGE, syncCounts);

  // Delegated click — [data-cart-add] on any button anywhere.
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-cart-add]');
    if (!btn) return;
    // Always cancel default so we don't accidentally follow a wrapping
    // <a href> to the product page.
    e.preventDefault();
    e.stopPropagation();

    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;

    const variantsCount = parseInt(btn.dataset.variantsCount || '1', 10);
    const variantId = btn.dataset.variantId;
    const productHandle = btn.dataset.productHandle;

    if (variantsCount > 1) {
      // Defer to the variant-picker modal — it decides which variant
      // + quantity to add and calls window.EraHugsCart.add() itself.
      dispatch(EVENT_PICK, { productHandle, sourceButton: btn });
      return;
    }
    if (!variantId) return;

    btn.classList.add('is-loading');
    btn.setAttribute('aria-busy', 'true');
    const result = await add(variantId, 1);
    btn.classList.remove('is-loading');
    btn.removeAttribute('aria-busy');
    if (result) {
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 1200);
    }
  });

  // Hydrate on page load so per-card badges + header count reflect
  // reality without a page navigation.
  fetchCart().then(() => dispatch(EVENT_CHANGE, cache));

  // Public API for scripts that need to read/mutate programmatically
  // (variant-picker modal, cart drawer, wishlist migration, WebMCP).
  window.EraHugsCart = {
    add,
    changeLine,
    get,
    fetchCart,
    quantityForVariant,
    openDrawer: () => dispatch(EVENT_OPEN_DRAWER, cache),
    EVENT_CHANGE,
    EVENT_PICK,
    EVENT_OPEN_DRAWER,
    EVENT_ADDED,
  };
})();
